import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ProfessionalReferralCard } from '../components/ProfessionalReferralCard';
import { FaSignOutAlt, FaArrowLeft } from 'react-icons/fa';
import {
  LoadingScreen,
  NoProfileScreen,
  PendingApprovalScreen,
} from '../components/pro/dashboard/ProDashboardStatusScreens';
import { ProDashSummary } from '../components/pro/dashboard/ProDashSummary';
import { ProDashOverviewTab } from '../components/pro/dashboard/ProDashOverviewTab';
import { ProDashConsultationsTab } from '../components/pro/dashboard/ProDashConsultationsTab';
import { ProDashWebinarsTab } from '../components/pro/dashboard/ProDashWebinarsTab';
import { ProDashProfileTab } from '../components/pro/dashboard/ProDashProfileTab';
import { BrandLogo } from '../components/BrandLogo';
import { StandardsGate, ProStatusNotice } from '../components/pro/StandardsGate';
import { PilotProCard } from '../components/pro/dashboard/PilotProCard';

const API = '/api';

export default function ProfessionalDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [myWebinars, setMyWebinars] = useState([]);
  const [stats, setStats] = useState({ upcoming: 0, completed: 0, pending: 0 });
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  const [showWebinarForm, setShowWebinarForm] = useState(false);
  const [webinarSubmitting, setWebinarSubmitting] = useState(false);
  const [webinarForm, setWebinarForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration_minutes: 60,
    category: '',
    max_attendees: 100,
    meeting_link: ''
  });

  const sessionToken = localStorage.getItem('session_token');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch professional profile
      const profileRes = await axios.get(`${API}/directory/my-profile`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setProfile(profileRes.data);

      // Fetch consultations
      try {
        const consultRes = await axios.get(`${API}/directory/my-consultations`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setConsultations(consultRes.data || []);

        // Calculate stats
        const now = new Date();
        const upcoming = consultRes.data?.filter(c => new Date(c.scheduled_date) > now && c.status === 'confirmed').length || 0;
        const completed = consultRes.data?.filter(c => c.status === 'completed').length || 0;
        const pending = consultRes.data?.filter(c => c.status === 'pending').length || 0;
        setStats({ upcoming, completed, pending });
      } catch (e) {
        // Consultations endpoint might not have data yet
      }

      // Fetch my webinar proposals
      try {
        const webinarsRes = await axios.get(`${API}/professionals/webinars/my-proposals`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setMyWebinars(webinarsRes.data || []);
      } catch (e) {
        // No webinars yet
      }

    } catch (err) {
      if (err.response?.status === 404) {
        setError('no_profile');
      } else if (err.response?.status === 403) {
        setError('not_approved');
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleWebinarSubmit = async (e) => {
    e.preventDefault();
    setWebinarSubmitting(true);

    try {
      await axios.post(`${API}/professionals/webinars/propose`, webinarForm, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      // Reset form and close modal
      setWebinarForm({
        title: '',
        description: '',
        date: '',
        time: '',
        duration_minutes: 60,
        category: '',
        max_attendees: 100,
        meeting_link: ''
      });
      setShowWebinarForm(false);

      // Refresh webinars list
      const webinarsRes = await axios.get(`${API}/professionals/webinars/my-proposals`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMyWebinars(webinarsRes.data || []);

      alert('Webinar proposal submitted! It will be reviewed by an admin.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit webinar proposal');
    } finally {
      setWebinarSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (error === 'no_profile') return <NoProfileScreen />;
  if (error === 'not_approved' || profile?.status === 'pending') return <PendingApprovalScreen />;

  const professionalType = profile?.professional_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div className="min-h-screen bg-gray-50">
      <StandardsGate open={!!profile && !profile.standards_accepted_at} onAccepted={fetchDashboardData} />
      <div className="container mx-auto px-4 pt-4"><ProStatusNotice profile={profile} /></div>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <BrandLogo className="w-9 h-9" testId="pro-dashboard-logo" />
              <span className="text-xl font-bold text-gray-800">Legacy Forge</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg font-medium transition text-xs sm:text-sm"
                data-testid="back-to-family-dashboard"
              >
                <FaArrowLeft className="text-sm" />
                <span className="hidden sm:inline">Family Dashboard</span>
                <span className="sm:hidden">Family</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-xs sm:text-sm"
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <ProDashSummary profile={profile} professionalType={professionalType} stats={stats} />
        <PilotProCard />

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['overview', 'consultations', 'webinars', 'referrals', 'profile'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'webinars' && myWebinars.filter(w => w.status === 'pending_approval').length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  {myWebinars.filter(w => w.status === 'pending_approval').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <ProDashOverviewTab profile={profile} professionalType={professionalType} setActiveTab={setActiveTab} />
        )}

        {activeTab === 'consultations' && (
          <ProDashConsultationsTab consultations={consultations} />
        )}

        {activeTab === 'referrals' && (
          <ProfessionalReferralCard sessionToken={sessionToken} />
        )}

        {activeTab === 'webinars' && (
          <ProDashWebinarsTab
            myWebinars={myWebinars}
            showWebinarForm={showWebinarForm}
            setShowWebinarForm={setShowWebinarForm}
            webinarForm={webinarForm}
            setWebinarForm={setWebinarForm}
            handleWebinarSubmit={handleWebinarSubmit}
            webinarSubmitting={webinarSubmitting}
          />
        )}

        {activeTab === 'profile' && (
          <ProDashProfileTab profile={profile} professionalType={professionalType} />
        )}
      </main>
    </div>
  );
}
